/**
 * Monta os valores das meta tags a partir do conteúdo.
 *
 * É função pura de propósito: a regra de SEO é o que mais varia e o que mais
 * precisa de teste, e testá-la aqui não exige renderizar componente, montar
 * store nem inspecionar o Helmet.
 *
 * OS 0007/2026 — Capítulo III, Seção II e Seção V.
 */

import { flattenToAppURL, toPublicURL } from '@plone/volto/helpers/Url/Url';

import {
  ARTICLE_PORTAL_TYPES,
  DEFAULT_OG_LOCALE,
  DEFAULT_OG_TYPE,
  DEFAULT_SITE_NAME,
  OG_LOCALE_BY_LANGUAGE,
  OG_TYPE_BY_PORTAL_TYPE,
  TITLE_SEPARATOR,
} from './constants';
import { OgImage, resolveOgImage } from './ogImage';
import { singleLine, titleOf, tokenOf } from './text';

export interface SeoMetadataOptions {
  siteName?: string;
  /**
   * Caminho que representa a página para o visitante. Numa pasta com item
   * padrão é a URL da PASTA, não a do item: é ela que o usuário acessa e
   * compartilha, então é ela que deve ser a canônica.
   */
  canonicalPath?: string;
  fallbackImageUrl?: string;
}

export interface SeoMetadata {
  title: string;
  description: string;
  canonicalUrl?: string;
  identifier?: string;
  robots: string;
  ogTitle: string;
  ogDescription: string;
  ogType: string;
  ogLocale: string;
  siteName: string;
  image: OgImage | null;
  isArticle: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags: string[];
  twitterCard: 'summary' | 'summary_large_image';
}

/**
 * O `@id` da raiz do site achata para string vazia, que sem tratamento cai
 * como falsy e deixava a home sem `canonical` e sem `og:url` — justamente a
 * URL mais compartilhada do portal.
 */
const buildCanonicalUrl = (path?: string): string | undefined => {
  if (path === undefined || path === null) return undefined;
  return toPublicURL(flattenToAppURL(path) || '/');
};

/** Não repetir o nome do site quando a própria página já é ele (a home). */
export const buildTitle = (pageTitle: string, siteName: string): string => {
  const base = singleLine(pageTitle);
  if (!base) return siteName;
  return base === siteName ? base : `${base} ${TITLE_SEPARATOR} ${siteName}`;
};

export const buildSeoMetadata = (
  content: any,
  { siteName, canonicalPath, fallbackImageUrl }: SeoMetadataOptions = {},
): SeoMetadata | null => {
  if (!content) return null;

  const site = siteName || DEFAULT_SITE_NAME;
  const portalType = content['@type'];
  const isArticle = ARTICLE_PORTAL_TYPES.includes(portalType);

  const description = singleLine(
    content.seo_description || content.description,
  );
  const ogDescription = singleLine(
    content.opengraph_description ||
      content.seo_description ||
      content.description,
  );

  const canonicalUrl =
    content.seo_canonical_url ||
    buildCanonicalUrl(canonicalPath ?? content['@id']);

  const language = tokenOf(content.language);
  const image = resolveOgImage(content, fallbackImageUrl);
  const tags: string[] = Array.isArray(content.subjects)
    ? content.subjects
    : [];

  return {
    title: buildTitle(content.seo_title || content.title, site),
    description,
    canonicalUrl,
    identifier: content.UID,
    // A diretiva positiva é explícita de propósito: sem ela, a política de
    // indexação da página fica implícita para o rastreador.
    robots: content.seo_noindex ? 'noindex, follow' : 'index, follow',
    ogTitle: singleLine(
      content.opengraph_title || content.seo_title || content.title,
    ),
    ogDescription,
    ogType: OG_TYPE_BY_PORTAL_TYPE[portalType] ?? DEFAULT_OG_TYPE,
    ogLocale:
      (language && OG_LOCALE_BY_LANGUAGE[language]) || DEFAULT_OG_LOCALE,
    siteName: site,
    image,
    isArticle,
    publishedTime: isArticle ? content.effective || undefined : undefined,
    modifiedTime: isArticle ? content.modified || undefined : undefined,
    section: isArticle
      ? singleLine(titleOf(content.tema)) || undefined
      : undefined,
    tags: isArticle ? tags : [],
    // `summary_large_image` sem imagem é promessa falsa: o card sai vazio.
    twitterCard: image ? 'summary_large_image' : 'summary',
  };
};

export default buildSeoMetadata;
